import { IYourAppServer } from "../YourAppServer.service";
import { UserInfoData, UserInfoModel3, SetUserInfoModel, SetCustomerMainAddressSuccessModel, SetCustomerMainAddressDataModel, SetCustomerFurtherAddressDataModel, SetCustomerFurtherAddressSuccessModel, UserInfoViewModel, UserDetailInfoViewModel, RemoveCustomerFurtherAddressSuccessModel, RemoveCustomerFurtherAddressDataModel } from "../../shared/models/userInfo.model";
import { GetAccountPaymentActivationStatusDataModel, RequestAccountPaymentActivationDataModel, AccountPaymentCreditDataModel } from "../../shared/models/accountPayment.model";
import Utils from "../../shared/utils";
import { StorageHive, LocalKeys } from "../valueStorage.service";
import { Gender } from "../../shared/contentTypes";
import { Observable, Subject } from "rxjs";
import { InternationalisationService } from "../../forked/angular-internationalisation";

export class YourAppServerUser {

  private _init: boolean = false;
  private _userInfo: UserInfoData = null;

  constructor(public readonly YourAppServer: IYourAppServer) {
  }

  /** Use TriggerUserDataChanged for trigger */
  private _userDataChanged: Subject<UserInfoData> = new Subject();
  /** Triggert das ObserveUserDataChanged event, wenn die UserInformation geändert werden */
  private TriggerUserDataChanged(): void {
    this._userDataChanged.next(this._userInfo);
  }
  public get ObserveUserDataChanged(): Observable<UserInfoData> {
    return this._userDataChanged;
  }

  /** Läd die Userdaten vom server (Auth is need) */
  private LoadUserData(): Promise<UserInfoData> {
    const me = this;
    return new Promise<UserInfoData>((resolve, reject) => {
      if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
        // ohne internet ist ein abruf nicht möglich
        return resolve(Utils.GetCopy(me._userInfo));
      } else if (!me.YourAppServer.Auth.IsActive) {
        // ohne Authentifizieren ist ein abruf unnötig
        return resolve(Utils.GetCopy(me._userInfo));
      } else if (me.YourAppServer.Auth.IsNobody) {
        // nobody hat keine daten
        return resolve(Utils.GetCopy(me._userInfo));
      }

      const FunctionName = "account/GetUserInfo3";
      me.YourAppServer.Api.RequestService(UserInfoModel3, FunctionName)
        .then((response) => {
          if (response.OK) {
            let userInfo = me._userInfo;
            if (Utils.IsNull(userInfo)) {
              userInfo = new UserInfoData();
            }
            UserInfoData.ReadFromModel(userInfo, response.Data);
            me.YourAppServer.coreCOMService.environment.valueStorage.set(StorageHive.Local, LocalKeys.yourAppUserData, userInfo)
              .catch(e => {
                me.YourAppServer.coreLogService.error("YourAppServerUser.LoadUserData", "environment.valueStorage.set", e);
              });
            me._userInfo = userInfo;
            resolve(Utils.GetCopy(me._userInfo));
          } else {
            me.YourAppServer.coreLogService.error("YourAppServerUser.LoadUserData", "RequestService (response not OK)", response);
            reject(response.HttpResponse.codeText);
          }
        })
        .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerUser.LoadUserData", "RequestService", error); reject(); });
    });
  }

  /** Läd bei bedarf die aktuellen daten in den server (Auth is need) */
  private UploadUserData(): Promise<UserInfoData> {
    const me = this;
    return new Promise<UserInfoData>((resolve, reject) => {
      if (!me._userInfo.need_upload) {
        // no data for upload
        return resolve(Utils.GetCopy(me._userInfo));
      } else if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) {
        // ohne internet ist ein abruf nicht möglich
        return resolve(Utils.GetCopy(me._userInfo));
      } else if (!me.YourAppServer.Auth.IsActive) {
        // ohne Authentifizieren ist ein abruf unnötig
        return resolve(Utils.GetCopy(me._userInfo));
      } else if (me.YourAppServer.Auth.IsNobody) {
        // nobody hat keine daten
        return resolve(Utils.GetCopy(me._userInfo));
      }

      const FunctionName = "account/SetUserInfo";
      const setModel = new SetUserInfoModel();
      setModel.version = 100; // 100 = {name: string, fname: string, bday_ymd: int, gender: int, zip_code: string}
      setModel.data = {};
      setModel.data["name"] = me._userInfo.name;
      setModel.data["fname"] = me._userInfo.fname;
      setModel.data["bday_ymd"] = me._userInfo.bday_ymd;
      setModel.data["gender"] = me._userInfo.gender;
      setModel.data["zip_code"] = me._userInfo.zip_code;

      setModel.data["prefix"] = me._userInfo.prefix;
      setModel.data["co"] = me._userInfo.co;
      setModel.data["address"] = me._userInfo.address;
      setModel.data["addition"] = me._userInfo.addition;
      setModel.data["city"] = me._userInfo.city;
      setModel.data["country"] = me._userInfo.country;
      setModel.data["telephone1"] = me._userInfo.telephone1;
      setModel.data["telephone2"] = me._userInfo.telephone2;
      setModel.data["telephone_mobile"] = me._userInfo.telephone_mobile;

      me.YourAppServer.Api.RequestService(UserInfoModel3, FunctionName, setModel)
        .then((response) => {
          if (response.OK) {
            UserInfoData.ReadFromModel(me._userInfo, response.Data);
            me.YourAppServer.coreCOMService.environment.valueStorage.set(StorageHive.Local, LocalKeys.yourAppUserData, Utils.GetCopy(me._userInfo));
            resolve(Utils.GetCopy(me._userInfo));
            me.TriggerUserDataChanged();
          } else {
            me.YourAppServer.coreLogService.error("YourAppServerUser.UploadUserData", "RequestService (response not OK)", response);
            reject(response.HttpResponse.codeText);
          }
        })
        .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerUser.UploadUserData", "RequestService", error); reject(); });
    });
  }

  /** gibt die gespeicherte UserInfo frei, muss z.B. beim logaout durchgeführt werden */
  public ClearData(): Promise<void> {
    const me = this;
    me._userInfo = null;
    return me.YourAppServer.coreCOMService.environment.valueStorage.remove(StorageHive.Local, LocalKeys.yourAppUserData);
  }

  /** initialisiert sich zum aller ersten mal */
  private Init(): Promise<void> {
    const me = this;
    if (me._init) {
      return Promise.resolve();
    }
    me._init = true;

    return new Promise<void>((resolve) => {
      // try load from storage
      me.YourAppServer.coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.yourAppUserData)
        .then((storageUserInfo) => {
          if (!Utils.IsNull(storageUserInfo)) {
            me._userInfo = Utils.DeepExtend(new UserInfoData(), storageUserInfo);
            if (me._userInfo.need_upload) {
              me.UploadUserData();
            }
          }
          resolve();
        })
        .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerUser.Init", "valueStorage.get", error); resolve(); });
    });
  }

  /** return die userInfo, läd es bei bedarf vom server
   * @param force erzwingt das herunterladen vom server
   * @returns userInfo copy (änderungen nür über Setter möglich bsp.: SetUserInfoFname)
   */
  public GetUserInfo(force: boolean = false): Promise<UserInfoData> {
    const me = this;
    return new Promise<UserInfoData>((resolve, reject) => {
      me.Init().then(() => {
        if (!force && !Utils.IsNull(me._userInfo)) {
          return resolve(Utils.GetCopy(me._userInfo));
        }
        me.LoadUserData().then(resolve).catch(reject);
      }).catch(reject);
    });
  }

  /** save user information */
  public SaveUserInfo(userInfoViewModel: UserDetailInfoViewModel): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      if (!Utils.IsNull(me._userInfo)) {
        me._userInfo.fname = userInfoViewModel.fname;
        me._userInfo.name = userInfoViewModel.name;
        me._userInfo.bday_ymd = userInfoViewModel.bday_ymd;
        me._userInfo.gender = userInfoViewModel.gender;
        me._userInfo.need_upload = true;
        me.UploadUserData()
          .then(() => {
            resolve();
          })
          .catch(error => {
            me.YourAppServer.coreLogService.error("YourAppServerUser.SaveUserInfo", "UploadUserData", error);
            reject();
          });
      }
    });
  }

  /** Setzen vom Nachnamen */
  public SetUserInfoName(name: string): void {
    const me = this;
    if (!Utils.IsNull(me._userInfo)) {
      me._userInfo.name = name;
      me._userInfo.need_upload = true;
      me.UploadUserData();
    }
  }

  /** Setzen vom Vornamen */
  public SetUserInfoFname(fname: string): void {
    const me = this;
    if (!Utils.IsNull(me._userInfo)) {
      me._userInfo.fname = fname;
      me._userInfo.need_upload = true;
      me.UploadUserData();
      // Vornamen wird bei der Authentifikation benötigt
      // darum nach der Änderung sofort dem Auth mitteilen
      me.YourAppServer.Auth.ReadUserNameFromUser();
    }
  }

  /** Setzen vom Geburtstag */
  public SetUserInfoBirthday(bday_ymd: number): void {
    const me = this;
    if (!Utils.IsNull(me._userInfo)) {
      me._userInfo.bday_ymd = bday_ymd;
      me._userInfo.need_upload = true;
      me.UploadUserData();
    }
  }

  /** Setzen vom Geschlecht */
  public SetUserInfoGender(gender: Gender): void {
    const me = this;
    if (!Utils.IsNull(me._userInfo)) {
      me._userInfo.gender = gender;
      me._userInfo.need_upload = true;
      me.UploadUserData();
    }
  }

  /** Setzen vom Postleitzahl */
  public SetUserInfoZip(zip_code: string): void {
    const me = this;
    if (!Utils.IsNull(me._userInfo)) {
      me._userInfo.zip_code = zip_code;
      me._userInfo.need_upload = true;
      me.UploadUserData();
    }
  }

  public setUserRegistrationData(registrationData: { [key: string]: string | number }): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      if (Utils.IsNull(me._userInfo)) {
        reject("UserInfo is null");
        return;
      }

      if (!me.YourAppServer.Auth.IsActive || me.YourAppServer.Auth.IsNobody) {
        reject("User is not authenticated for registration data upload");
        return;
      }

      // Set data from registrationData to _userInfo
      for (const key in registrationData) {
        if (Object.prototype.hasOwnProperty.call(registrationData, key)) {
          me._userInfo[key] = registrationData[key];
        }
      }
      me._userInfo.need_upload = true;
      me.UploadUserData()
        .then(() => {
          resolve();
        })
        .catch(error => {
          me.YourAppServer.coreLogService.error("YourAppServerUser.SetUserRegistrationData", "UploadUserData", error);
          reject(error);
        });
    });
  }

  /** setzt oder aktualisiert die CustomerMainAddress */
  public SetCustomerMainAddress(address: { [key: string]: string | number }): Promise<SetCustomerMainAddressSuccessModel> {
    const me = this;
    return new Promise<SetCustomerMainAddressSuccessModel>((resolve, reject) => {
      const FunctionName = "account/SetCustomerMainAddress";
      const setModel = new SetCustomerMainAddressDataModel();
      setModel.address = address;

      me.YourAppServer.Api.RequestService(SetCustomerMainAddressSuccessModel, FunctionName, setModel)
        .then((response) => {
          if (response.OK) {
            me._userInfo = null; // cache wieder freigeben, da die Adresse sich geändert hat
            resolve(response.Data);
            me.TriggerUserDataChanged();
          } else {
            me.YourAppServer.coreLogService.error("YourAppServerUser.SetCustomerMainAddress", "RequestService (response not OK)", response);
            reject(response.HttpResponse.codeText);
          }
        })
        .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerUser.SetCustomerMainAddress", "RequestService", error); reject(); });
    });
  }

  /** setzt oder aktualisiert eine CustomerFurtherAddress
   * token="" if new
   */
  public SetCustomerFurtherAddressDataModel(token: string, address: { [key: string]: string | number }): Promise<SetCustomerFurtherAddressSuccessModel> {
    const me = this;
    return new Promise<SetCustomerFurtherAddressSuccessModel>((resolve, reject) => {
      const FunctionName = "account/SetCustomerFurtherAddress";
      const setModel = new SetCustomerFurtherAddressDataModel();
      setModel.address = address;
      setModel.token = token;

      me.YourAppServer.Api.RequestService(SetCustomerFurtherAddressSuccessModel, FunctionName, setModel)
        .then((response) => {
          if (response.OK) {
            me._userInfo = null; // cache wieder freigeben, da die Adresse sich geändert hat
            resolve(response.Data);
            me.TriggerUserDataChanged();
          } else {
            me.YourAppServer.coreLogService.error("YourAppServerUser.SetCustomerFurtherAddressDataModel", "RequestService (response not OK)", response);
            reject(response.HttpResponse.codeText);
          }
        })
        .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerUser.SetCustomerFurtherAddressDataModel", "RequestService", error); reject(); });
    });
  }

  /** Returns the current account-payment activation status for the authenticated customer. */
  public async GetAccountPaymentActivationStatus(): Promise<GetAccountPaymentActivationStatusDataModel> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) { throw "offline"; }
    if (!me.YourAppServer.Auth.IsActive) { throw "not authenticated"; }
    if (me.YourAppServer.Auth.IsNobody) { throw "nobody"; }
    const response = await me.YourAppServer.Api.RequestService(GetAccountPaymentActivationStatusDataModel, "account/GetAccountingStatus", {});
    if (response.OK) { return response.Data; }
    me.YourAppServer.coreLogService.error(
      "YourAppServerUser.GetAccountPaymentActivationStatus",
      "RequestService (response not OK)",
      response.HttpResponse.codeText || response.HttpResponse.code
    );
    throw response.HttpResponse.codeText;
  }

  /** Sends an activation request; the server returns AccountingStatusModel { status } on the same route as GetAccountingStatus. */
  public async RequestAccountPaymentActivation(): Promise<RequestAccountPaymentActivationDataModel> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) { throw "offline"; }
    if (!me.YourAppServer.Auth.IsActive) { throw "not authenticated"; }
    if (me.YourAppServer.Auth.IsNobody) { throw "nobody"; }
    const response = await me.YourAppServer.Api.RequestService(RequestAccountPaymentActivationDataModel, "account/RequestAccountingActivation", {
      language: InternationalisationService.locale,
    });
    if (response.OK) { return response.Data; }
    me.YourAppServer.coreLogService.error(
      "YourAppServerUser.RequestAccountPaymentActivation",
      "RequestService (response not OK)",
      response.HttpResponse.codeText || response.HttpResponse.code
    );
    throw response.HttpResponse.codeText;
  }

  /** Credit amounts are integer cents; route: account/GetAccountingInfo. */
  public async GetAccountPaymentCredit(): Promise<AccountPaymentCreditDataModel> {
    const me = this;
    if (!me.YourAppServer.coreCOMService.environment.networkInformation.isOnline) { throw "offline"; }
    if (!me.YourAppServer.Auth.IsActive) { throw "not authenticated"; }
    if (me.YourAppServer.Auth.IsNobody) { throw "nobody"; }
    const response = await me.YourAppServer.Api.RequestService(AccountPaymentCreditDataModel, "account/GetAccountingInfo", {});
    if (response.OK) { return response.Data; }
    me.YourAppServer.coreLogService.error(
      "YourAppServerUser.GetAccountPaymentCredit",
      "RequestService (response not OK)",
      response.HttpResponse.codeText || response.HttpResponse.code
    );
    throw response.HttpResponse.codeText;
  }

  /** remove a delivery address base on token
 * token delivery address token
 */
  public RemoveCustomerFurtherAddressDataModel(token: string): Promise<RemoveCustomerFurtherAddressSuccessModel> {
    const me = this;
    return new Promise<RemoveCustomerFurtherAddressSuccessModel>((resolve, reject) => {
      const FunctionName = "account/RemoveCustomerFurtherAddress";
      const RequestData = new RemoveCustomerFurtherAddressDataModel();
      RequestData.token = token;

      me.YourAppServer.Api.RequestService(RemoveCustomerFurtherAddressSuccessModel, FunctionName, RequestData)
        .then((response) => {
          if (response.OK) {
            if (response.Data.success) {
              me._userInfo = null; // cache wieder freigeben, da die Adresse sich geändert hat
              resolve(response.Data);
              me.TriggerUserDataChanged();
            } else {
              me.YourAppServer.coreLogService.error("YourAppServerUser.RemoveCustomerFurtherAddressDataModel", "RequestService (responde data not success)", response.Data.error);
              reject(response.Data.error);
            }
          } else {
            me.YourAppServer.coreLogService.error("YourAppServerUser.RemoveCustomerFurtherAddressDataModel", "RequestService (response not OK)", response);
            reject(response.HttpResponse.codeText);
          }
        })
        .catch((error) => { me.YourAppServer.coreLogService.error("YourAppServerUser.RemoveCustomerFurtherAddressDataModel", "RequestService", error); reject(); });
    });
  }
}
